"use client";

import { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  IconButton,
  Grid,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  CloudUpload,
  Close,
  InsertDriveFile,
  Image,
  VideoLibrary,
  Delete,
} from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import uploadFile from "../../lib/fileUpload";
import { updateProject, uploadProjectFiles } from "../../apis/apisList/userApi";
import { toast } from "react-toastify";

// File type icons mapping
const fileTypeIcons = {
  image: <Image fontSize="large" color="primary" />,
  video: <VideoLibrary fontSize="large" color="primary" />,
  default: <InsertDriveFile fontSize="large" color="primary" />,
};

// Function to determine file type
const getFileType = (file) => {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  return "other";
};

// Function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (
    Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  );
};

export default function FileUploadDialog({
  open,
  onClose,
  projectId,
  planType,
  project,
}) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  // Prevent dialog close during upload
  const handleClose = () => {
    if (!uploading) {
      onClose();
    }
  };

  // Handle file drop
  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        id: `${file.name}-${Date.now()}`,
      })
    );

    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openDialog,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: {},
    noClick: false,
    noKeyboard: false,
    preventDropOnDocument: true,
  });

  // Remove file
  const removeFile = (id) => {
    setFiles(files.filter((file) => file.id !== id));
  };

  const generateVideoThumbnail = (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.src = URL.createObjectURL(file);
      video.crossOrigin = "anonymous";
      video.muted = true;
      video.currentTime = 1; // Seek to 1 second for thumbnail

      video.onloadeddata = () => {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            resolve(blob);
          },
          "image/jpeg",
          0.8
        );
      };

      video.onerror = (e) => {
        reject("Failed to load video for thumbnail");
      };
    });
  };

  // Upload files to S3
  const uploadToS3 = async () => {
    if (files.length === 0) return;
    if (!projectId) return;

    setUploading(true);
    setUploadProgress({});

    try {
      const uploadedFiles = await Promise.all(
        files.map(async (file) => {
          return new Promise(async (resolve, reject) => {
            try {
              const fileType = getFileType(file);
              const folderStrucuture = `${
                project?.contact?.email
              }/${project?.name
                ?.toLowerCase()
                .replace(/\s+/g, "_")
                .replace(/\//g, "")}`;
              const fileUrl = await uploadFile(
                file,
                folderStrucuture,
                (progress) => {
                  setUploadProgress((prev) => ({
                    ...prev,
                    [file.id]: progress,
                  }));
                }
              );

              let thumbnailUrl = null;
              if (fileType === "video") {
                const thumbnailBlob = await generateVideoThumbnail(file);
                const thumbnailFile = new File(
                  [thumbnailBlob],
                  `${file.name}.jpg`,
                  { type: "image/jpeg" }
                );
                thumbnailUrl = await uploadFile(
                  thumbnailFile,
                  folderStrucuture,
                  () => {}
                );
              }

              resolve({
                name: file.name,
                type: fileType,
                url: fileUrl,
                size: formatFileSize(file.size),
                ext: file.name.substring(file.name.lastIndexOf(".")),
                projectId,
                planType,
                thumbnail: thumbnailUrl,
              });
            } catch (error) {
              reject(error);
            }
          });
        })
      );

      await uploadProjectFiles(projectId, uploadedFiles);

      setUploading(false);
      toast.success("Files Uploaded successfully");

      setTimeout(() => {
        setFiles([]);
        setUploadProgress({});
        onClose();
      }, 300);
    } catch (error) {
      toast.error("Error uploading files");
      console.error("Upload failed:", error);
      setUploading(false);
    }
  };

  // Render file preview
  const renderFilePreview = (file) => {
    const fileType = getFileType(file);

    return (
      <Grid item xs={12} sm={6} md={4} key={file.id}>
        <Paper
          elevation={3}
          sx={{
            p: 2,
            position: "relative",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <IconButton
            size="small"
            onClick={() => removeFile(file.id)}
            disabled={uploading}
            sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
          >
            <Delete />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
              flexGrow: 1,
              minHeight: 100,
            }}
          >
            {fileType === "image" ? (
              <Box
                component="img"
                src={file.preview}
                alt={file.name}
                sx={{
                  maxHeight: 120,
                  maxWidth: "100%",
                  objectFit: "contain",
                }}
              />
            ) : fileType === "video" ? (
              <Box
                component="video"
                src={file.preview}
                controls
                sx={{
                  maxHeight: 120,
                  maxWidth: "100%",
                }}
              />
            ) : (
              fileTypeIcons[fileType]
            )}
          </Box>

          <Typography variant="body2" noWrap title={file.name}>
            {file.name}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            {formatFileSize(file.size)}
          </Typography>

          {uploading && (
            <Box sx={{ width: "100%", mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={uploadProgress[file.id] || 0}
              />
              <Typography variant="caption" align="center" display="block">
                {Math.round(uploadProgress[file.id] || 0)}%
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>
    );
  };

  // Handle drops from outside the browser window
  useEffect(() => {
    const handleWindowDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleWindowDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    window.addEventListener("dragover", handleWindowDragOver);
    window.addEventListener("drop", handleWindowDrop);

    return () => {
      window.removeEventListener("dragover", handleWindowDragOver);
      window.removeEventListener("drop", handleWindowDrop);
    };
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: "60vh",
        },
      }}
    >
      <DialogTitle>
        Upload Files
        {!uploading && (
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <Close />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {/* Drag & Drop Area */}
        <Box
          {...getRootProps()}
          sx={{
            border: "2px dashed",
            borderColor: isDragAccept
              ? "success.main"
              : isDragReject
              ? "error.main"
              : isDragActive
              ? "primary.main"
              : "grey.300",
            borderRadius: 2,
            p: 3,
            mb: 3,
            textAlign: "center",
            cursor: "pointer",
            bgcolor: isDragActive
              ? "rgba(25, 118, 210, 0.08)"
              : isDragAccept
              ? "rgba(76, 175, 80, 0.08)"
              : isDragReject
              ? "rgba(239, 83, 80, 0.08)"
              : "transparent",
            transition: "all 0.2s ease",
            outline: "none",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "rgba(25, 118, 210, 0.04)",
            },
            "&:focus": {
              borderColor: "primary.main",
              boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.2)",
              outline: "none",
            },
          }}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openDialog();
            }
          }}
        >
          <input {...getInputProps()} />
          <CloudUpload
            color={
              isDragReject ? "error" : isDragAccept ? "success" : "primary"
            }
            sx={{
              fontSize: 48,
              mb: 2,
              animation: isDragActive ? "pulse 1.5s infinite" : "none",
              "@keyframes pulse": {
                "0%": {
                  transform: "scale(1)",
                },
                "50%": {
                  transform: "scale(1.1)",
                },
                "100%": {
                  transform: "scale(1)",
                },
              },
            }}
          />
          <Typography variant="h6" gutterBottom>
            {isDragActive
              ? isDragReject
                ? "Some files are not supported"
                : "Drop files here..."
              : "Drag & Drop Files Here"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            or click to select files
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Supports images, videos, documents, and more
          </Typography>
        </Box>

        {/* Alternative file selection button */}
        <Box sx={{ textAlign: "center", mb: 3 }} {...getRootProps()}>
          {/* <Button
            variant="outlined"
            // onClick={openDialog}
            startIcon={<CloudUpload />}
            disabled={uploading}
          > */}
          <input {...getInputProps()} />
          {/* Browse Files */}
          {/* </Button> */}
        </Box>

        {/* File Previews */}
        {files.length > 0 && (
          <Grid container spacing={2}>
            {files.map(renderFilePreview)}
          </Grid>
        )}

        {/* Empty State */}
        {files.length === 0 && !uploading && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="text.secondary">No files selected</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Typography variant="caption" color="text.secondary">
          {files.length > 0
            ? `${files.length} ${
                files.length === 1 ? "file" : "files"
              } selected`
            : ""}
        </Typography>
        <Box>
          <Button onClick={handleClose} disabled={uploading} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={uploadToS3}
            disabled={files.length === 0 || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : null}
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
